/**
 * My Certifications — one place for everything the signed-in employee holds.
 *
 * ── Why this page exists ──────────────────────────────────────────────────────────────────
 *
 * A certification reaches an employee's history by one of two routes, and until now each route
 * had its own page:
 *
 *   • Apply for Reimbursement  →  a claim, with a Manager → Admin → Finance approval pipeline
 *   • Add Certification        →  a direct record, no approval, no money
 *
 * All Certifications shows the whole ORGANISATION's register (everyone, passed only). The
 * Reimbursement page shows only the CLAIMS. Neither answers the plain question an employee
 * actually asks — "what certifications do I have, however they got here, and where does each
 * one stand?" This page answers exactly that: it is scoped to the signed-in person and unites
 * both routes in one list.
 *
 * ── Two records, one row ──────────────────────────────────────────────────────────────────
 *
 * The qualification (Certification) is the source of truth for the exam — name, provider,
 * result, dates, document, expiry. The reimbursement claim is a separate record that references
 * it by `certificationId` and carries the APPROVAL status. We load both, join claim→certification
 * by that id, and show the approval status only on the rows that have a claim. A directly-added
 * record simply has no claim and no status, which is correct — there is nothing to approve.
 *
 * Result (Passed/Failed) and approval status (Pending/Reimbursed/Rejected) are kept as different
 * shapes, exactly as the Reimbursement page does: a failed exam is not a rejected claim.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { canonicalizeEmail } from '../../services/identity';
import { Certification, CertificationResult, CertificationSource } from '../../types/certification';
import {
  CertificationReimbursement,
  ReimbursementStatus,
} from '../../types/certificationReimbursement';
import { CertificationService, describeCertificationError } from '../../services/certificationService';
import { CertificationReimbursementService } from '../../services/certificationReimbursementService';
import { getCertificateUrl } from '../../services/certificationDocuments';
import CertificateViewerModal, { ViewingCertificate } from './CertificateViewerModal';
import './Certifications.css';

const formatDate = (iso?: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!match) return '—';
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${Number(match[3])}-${MONTHS[Number(match[2]) - 1]}-${match[1]}`;
};

const money = (amount: number, currency: string) =>
  `${currency === 'INR' ? '₹' : `${currency} `}${Number(amount || 0).toLocaleString()}`;

/** Approval status → the three status tones the certs stylesheet already defines. */
const statusTone = (status: ReimbursementStatus): 'ok' | 'wait' | 'no' => {
  if (status === ReimbursementStatus.REIMBURSED) return 'ok';
  if (status === ReimbursementStatus.REJECTED) return 'no';
  return 'wait';
};

/** The three lenses on the same list. Counts sit beside each so an empty one reads as empty. */
type Filter = 'all' | 'reimbursement' | 'record';

/** A certification paired with its reimbursement claim, when it has one. */
interface MyCertRow {
  certification: Certification;
  claim?: CertificationReimbursement;
  /** Came through Apply for Reimbursement — has a claim, or the record says so. */
  isReimbursement: boolean;
}

const MyCertificationsPage: React.FC = () => {
  const { user } = useAuth();
  const myEmail = canonicalizeEmail(user?.email);

  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [claims, setClaims] = useState<CertificationReimbursement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [openingDoc, setOpeningDoc] = useState<string | null>(null);
  const [docError, setDocError] = useState('');
  const [viewing, setViewing] = useState<ViewingCertificate | null>(null);

  const load = useCallback(async () => {
    if (!myEmail) return;
    setLoading(true);
    setError('');
    try {
      // Both are read through the byEmployee index — one round trip each, no org-wide scan.
      const [certs, myClaims] = await Promise.all([
        CertificationService.listForEmployee(myEmail),
        CertificationReimbursementService.listForEmployee(myEmail),
      ]);
      setCertifications(certs);
      setClaims(myClaims);
    } catch (err: any) {
      setError(describeCertificationError(err, 'Could not load your certifications.'));
      setCertifications([]);
      setClaims([]);
    } finally {
      setLoading(false);
    }
  }, [myEmail]);

  useEffect(() => {
    load();
  }, [load]);

  /** claim.certificationId is the only link between the two records — see the type's comment. */
  const claimByCertId = useMemo(() => {
    const map = new Map<string, CertificationReimbursement>();
    for (const claim of claims) map.set(claim.certificationId, claim);
    return map;
  }, [claims]);

  /** Every row, newest result first — the thing just added is the thing being checked. */
  const rows = useMemo<MyCertRow[]>(
    () =>
      certifications
        .map((certification) => {
          const claim = claimByCertId.get(certification.id);
          return {
            certification,
            claim,
            isReimbursement:
              Boolean(claim) || certification.source === CertificationSource.REIMBURSEMENT,
          };
        })
        .sort((a, b) =>
          String(b.certification.resultDate).localeCompare(String(a.certification.resultDate))
        ),
    [certifications, claimByCertId]
  );

  const counts = useMemo(
    () => ({
      all: rows.length,
      reimbursement: rows.filter((r) => r.isReimbursement).length,
      record: rows.filter((r) => !r.isReimbursement).length,
    }),
    [rows]
  );

  const visible = useMemo(() => {
    const byFilter = rows.filter((r) =>
      filter === 'all' ? true : filter === 'reimbursement' ? r.isReimbursement : !r.isReimbursement
    );
    const needle = search.trim().toLowerCase();
    if (!needle) return byFilter;
    return byFilter.filter((r) =>
      [r.certification.name, r.certification.provider, r.certification.result].some((field) =>
        String(field || '').toLowerCase().includes(needle)
      )
    );
  }, [rows, filter, search]);

  const openDocument = async (c: Certification) => {
    setDocError('');
    setOpeningDoc(c.id);
    try {
      const url = await getCertificateUrl(c.documentKey, c.documentName || c.name);
      setViewing({ url, title: c.name, documentName: c.documentName || undefined });
    } catch (err: any) {
      setDocError(err?.message || 'The document could not be opened.');
    } finally {
      setOpeningDoc(null);
    }
  };

  const FILTERS: { key: Filter; label: string }[] = [
    { key: 'all', label: `All (${counts.all})` },
    { key: 'reimbursement', label: `Reimbursements (${counts.reimbursement})` },
    { key: 'record', label: `Records (${counts.record})` },
  ];

  return (
    <div className="certs">
      <div className="certs__head">
        <div>
          <h1>My Certifications</h1>
          <p>
            Everything you hold — the ones you claimed reimbursement for and the ones you recorded
            yourself — with where each reimbursement has got to.
          </p>
        </div>
        <Link to="/certifications/reimbursement" className="certs__btn certs__btn--primary">
          Apply for Reimbursement
        </Link>
      </div>

      <div className="certs__filters">
        <input
          type="search"
          className="certs__search"
          placeholder="Search your certifications..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search your certifications"
        />
        <div className="certs__filters-group">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`certs__btn${filter === f.key ? ' certs__btn--on' : ''}`}
              onClick={() => setFilter(f.key)}
              aria-pressed={filter === f.key}
            >
              {f.label}
            </button>
          ))}
          <button type="button" className="certs__btn" onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="certs__notice certs__notice--error" role="alert">{error}</div>}
      {docError && <div className="certs__notice certs__notice--error" role="alert">{docError}</div>}

      {loading && certifications.length === 0 ? (
        <div className="certs__grid" aria-hidden="true">
          {[0, 1, 2, 3].map((n) => (
            <div key={n} className="certs__skeleton certs__skeleton--card" />
          ))}
        </div>
      ) : error ? null : visible.length === 0 ? (
        <div className="certs__empty">
          <h3>
            {rows.length === 0
              ? 'You have no certifications yet.'
              : 'Nothing matches that filter.'}
          </h3>
          <p>
            {rows.length === 0 ? (
              <>
                Record one you already hold from{' '}
                <Link className="certs__link" to="/certifications/all">
                  All Certifications
                </Link>
                , or claim the cost from{' '}
                <Link className="certs__link" to="/certifications/reimbursement">
                  Apply for Reimbursement
                </Link>
                .
              </>
            ) : (
              'Try a different search or filter.'
            )}
          </p>
        </div>
      ) : (
        <div className="certs__grid">
          {visible.map(({ certification: c, claim, isReimbursement }) => {
            const passed = c.result === CertificationResult.PASSED;
            return (
              <div key={c.id} className="certs__card">
                <div className="certs__card-body">
                  <div className="certs__card-title">{c.name}</div>
                  <div className="certs__card-person">{c.provider}</div>

                  <div className="certs__card-tags">
                    <span className={`certs__pill ${passed ? 'certs__pill--passed' : 'certs__pill--failed'}`}>
                      {c.result}
                    </span>
                    <span className="certs__tag certs__tag--muted">
                      {isReimbursement ? 'Reimbursement' : 'Record only'}
                    </span>
                    {claim && (
                      <span className={`certs__status certs__status--${statusTone(claim.status)}`}>
                        {claim.status}
                      </span>
                    )}
                  </div>

                  <div className="certs__card-facts">
                    <div>
                      <span className="certs__card-label">Result date</span>
                      <strong>{formatDate(c.resultDate)}</strong>
                    </div>
                    {c.expiryDate && (
                      <div>
                        <span className="certs__card-label">Expires</span>
                        <strong>{formatDate(c.expiryDate)}</strong>
                      </div>
                    )}
                    {claim && (
                      <div>
                        <span className="certs__card-label">Amount</span>
                        <strong>{money(claim.reimbursementAmount, claim.currency)}</strong>
                      </div>
                    )}
                  </div>

                  {claim?.rejectionReason && (
                    <p className="certs__muted">
                      <strong>Reason given:</strong> {claim.rejectionReason}
                    </p>
                  )}
                </div>

                <div className="certs__card-actions">
                  {c.documentKey ? (
                    <button
                      type="button"
                      className="certs__btn certs__btn--sm"
                      onClick={() => openDocument(c)}
                      disabled={openingDoc === c.id}
                    >
                      {openingDoc === c.id ? 'Opening…' : 'View certificate'}
                    </button>
                  ) : (
                    <span className="certs__muted">No document</span>
                  )}
                  {isReimbursement && (
                    <Link className="certs__btn certs__btn--sm certs__btn--quiet" to="/certifications/reimbursement">
                      Track claim
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {viewing && <CertificateViewerModal {...viewing} onClose={() => setViewing(null)} />}
    </div>
  );
};

export default MyCertificationsPage;
